/**
 * @fileoverview Type definitions and constants for the LoopAI AdStudio application
 */

export const CREDIT_COSTS = {
  SCRIPT_GENERATION: 50,
  CHARACTER_EXTRACTION: 20,
  IMAGE_GENERATION: 30,
  IMAGE_EDITING: 30,
  VOICE_CLONING: 100,
  VIDEO_GENERATION: 250,
  AUDIO_TRANSCRIPTION: 20,
  PROMPT_GENERATION: 10,
};

/**
 * @typedef {Object} AudioData
 * @property {string} name
 * @property {string} data - Base64 encoded audio
 * @property {string} mimeType
 */

/**
 * @typedef {Object} DialogueAudioPayload
 * @property {string} characterId
 * @property {string} text
 * @property {AudioData} audioData
 */

/**
 * @typedef {Object} TextOverlay
 * @property {string} id
 * @property {string} content
 * @property {boolean} aiStyle
 * @property {string} fontFamily
 * @property {number} fontSize
 * @property {string} color
 */

/**
 * @typedef {Object} ShotDialogue
 * @property {string} characterId
 * @property {string} text
 * @property {string} [audioAssetId]
 */

/**
 * @typedef {Object} Cinematography
 * @property {string} cameraAngle
 * @property {string} shotSize
 * @property {string} movement
 * @property {string} lens
 * @property {string} lighting
 */

/**
 * @typedef {Object} SoundDesign
 * @property {string} music
 * @property {string} sfx
 */

/**
 * @typedef {Object} Shot
 * @property {string} id
 * @property {string} description
 * @property {string} [startFramePrompt]
 * @property {string} [endFramePrompt]
 * @property {string[]} [characterIds]
 * @property {string[]} [elementIds]
 * @property {string} [startFrameImage]
 * @property {string} [endFrameImage]
 * @property {string} [video]
 * @property {string} [narration]
 * @property {string} [narrationAudioAssetId]
 * @property {ShotDialogue[]} [dialogues]
 * @property {TextOverlay[]} [textOverlays]
 * @property {Cinematography} [cinematography]
 * @property {SoundDesign} [sound]
 * @property {string} [vfx]
 */

/**
 * @typedef {Object} Scene
 * @property {string} id
 * @property {Shot[]} shots
 */

/**
 * @typedef {Object} Character
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {'character' | 'element' | 'narrator'} [category]
 * @property {string} [image]
 * @property {string[]} [referenceImages]
 * @property {string[]} [costumeAndAccessoryImages]
 * @property {'natural' | 'light' | 'heavy'} [makeupLevel]
 * @property {Object} [audio]
 * @property {string} audio.name
 * @property {string} [audio.data]
 * @property {string} [elevenLabsVoiceId]
 */

/**
 * @typedef {Object} ElevenLabsVoice
 * @property {string} voice_id
 * @property {string} name
 * @property {string} category
 * @property {Record<string, string>} labels
 * @property {string} preview_url
 */

/**
 * @typedef {'video' | 'audio' | 'text'} ClipType
 * @typedef {'video' | 'audio' | 'text'} TrackType
 * @typedef {'select' | 'split'} EditingTool
 */

/**
 * @typedef {Object} Transition
 * @property {'none' | 'fade-in' | 'fade-out' | 'cross-dissolve'} type
 * @property {number} duration
 */

/**
 * @typedef {Object} Clip
 * @property {string} id
 * @property {string} assetId
 * @property {ClipType} type
 * @property {string} trackId
 * @property {number} start
 * @property {number} duration
 */

/**
 * @typedef {Clip & {shotId: string, subtype?: 'video' | 'start' | 'end', sourceStart?: number, sourceDuration?: number, transition?: Transition, speed?: number}} VideoClip
 */

/**
 * @typedef {Clip & {name: string, volume: number}} AudioClip
 */

/**
 * @typedef {Clip & {content: string, fontSize: number, color: string, fontFamily: string}} TextClip
 */

/**
 * @typedef {VideoClip | AudioClip | TextClip} AnyClip
 */

/**
 * @typedef {Object} Track
 * @property {string} id
 * @property {TrackType} type
 * @property {string} name
 */

/**
 * @typedef {Object} Timeline
 * @property {Track[]} tracks
 * @property {AnyClip[]} clips
 * @property {number} duration
 */

/**
 * @typedef {Object} Asset
 * @property {string} id
 * @property {'audio' | 'video'} type
 * @property {string} name
 * @property {string} data
 */

/**
 * @typedef {Object} ModelPreferences
 * @property {Object} image
 * @property {Object} image.portrait
 * @property {string} image.portrait.textToImage
 * @property {string} image.portrait.referenceToImage
 * @property {Object} image.general
 * @property {string} image.general.textToImage
 * @property {string} image.general.referenceToImage
 * @property {Object} video
 * @property {string} video.general
 * @property {Object} [video.seedancePro]
 * @property {'480p' | '720p' | '1080p'} [video.seedancePro.resolution]
 * @property {Object} [video.sora2]
 * @property {'auto' | '720p'} [video.sora2.resolution]
 * @property {Object} [video.ltxFast]
 * @property {'1080p' | '1440p' | '2160p'} [video.ltxFast.resolution]
 * @property {Object} animate
 * @property {string} animate.general
 * @property {Object} [animate.wan22Animate]
 * @property {'480p' | '580p' | '720p'} [animate.wan22Animate.resolution]
 * @property {Object} [lipsync]
 * @property {string} [lipsync.model]
 * @property {Object} audio
 * @property {string} audio.tts
 * @property {string} audio.music
 * @property {string} audio.sfx
 */

/**
 * @typedef {'dashboard' | 'project' | 'models' | 'script' | 'characters' | 'storyboard' | 'studio'} WizardStep
 */

/**
 * @typedef {Object} Project
 * @property {string} id
 * @property {string} [projectId]
 * @property {string} name
 * @property {string} duration
 * @property {string} aspectRatio
 * @property {string} script
 * @property {Character[]} characters
 * @property {Scene[]} scenes
 * @property {Asset[]} assets
 * @property {Timeline} [timeline]
 * @property {ModelPreferences} [modelPreferences]
 * @property {number} lastModified
 * @property {WizardStep} [currentStep]
 */

/**
 * @typedef {Pick<Project, 'id' | 'name' | 'lastModified' | 'projectId'>} ProjectMetadata
 */

/**
 * @typedef {'idle' | 'transcribing' | 'character-generating' | 'storyboarding' | 'imaging' | 'video-generating' | 'done' | 'error'} GenerationState
 */

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} email
 * @property {string} name
 * @property {string} picture
 */